import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { UTCDate } from '@date-fns/utc';
import { differenceInDays, format } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import {
    BehaviorSubject,
    combineLatest,
    EMPTY,
    expand,
    interval,
    map,
    Observable,
    of,
    startWith,
    throwError,
    timer,
} from 'rxjs';
import { catchError, shareReplay, switchMap } from 'rxjs/operators';
import { ConnectionAutomationService } from '../connection-automation.service';
import { ConnectionState, GetConnectionResponse } from '../models/get-connection-response.model';

type ListConnectionsFilters = {
    onlySecretsAboutToExpire: FormControl<boolean>;
};

type ConnectionTestResult =
    | { status: 'testing'; requestPermissionUrl?: string }
    | { status: 'success'; requestPermissionUrl?: string }
    | { status: 'failed'; message?: string; requestPermissionUrl?: string };

@Component({
    selector: 'app-list-connections',
    imports: [
        MatMenuModule,
        MatIconModule,
        MatButtonModule,
        ReactiveFormsModule,
        CommonModule,
        MatSlideToggleModule,
        MatTableModule,
        MatProgressSpinner,
    ],
    templateUrl: './list-connections.component.html',
    styleUrls: ['./list-connections.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListConnectionsComponent implements OnInit {
    private readonly DAYS_NEAR_EXPIRATION = 14; // Days where a secret is considered to be "about to expire"
    private readonly refresh$ = new BehaviorSubject<void>(undefined);

    private readonly testResultsSubject = new BehaviorSubject<Record<string, ConnectionTestResult>>({});
    protected testResults$ = this.testResultsSubject.asObservable();

    protected filtersForm: FormGroup<ListConnectionsFilters>;

    protected connections$!: Observable<GetConnectionResponse[]>;
    public isLoading = signal<boolean>(true);
    protected displayedColumns: string[] = [
        'connectionAlias',
        'businessDomainName',
        'connectionHost',
        'connectionUsername',
        'secretDisplayName',
        'secretExpiresAt',
        'actions',
    ];

    public constructor(
        private readonly connectionAutomationService: ConnectionAutomationService,
        private readonly fb: FormBuilder,
        private readonly toastrService: ToastrService,
        private readonly router: Router,
    ) {
        this.filtersForm = this.fb.nonNullable.group({
            onlySecretsAboutToExpire: this.fb.nonNullable.control<boolean>(false),
        });
    }

    public ngOnInit(): void {
        this.connections$ = this.refresh$.pipe(
            switchMap(() =>
                this.getFilteredConnections().pipe(
                    expand((connections) =>
                        connections.some((connection) => connection.connection.state === ConnectionState.Pending)
                            ? timer(5000).pipe(switchMap(() => this.getFilteredConnections()))
                            : EMPTY,
                    ),
                ),
            ),
            shareReplay(1),
        );

        this.refresh$.next();
        interval(90000).subscribe(() => this.refresh$.next());
    }

    public getFilteredConnections(): Observable<GetConnectionResponse[]> {
        this.isLoading.set(true);

        const connections = combineLatest([
            this.connectionAutomationService.listConnections(),
            this.filtersForm.valueChanges.pipe(
                map((form) => form.onlySecretsAboutToExpire ?? false),
                startWith(false),
            ),
        ]).pipe(
            map(([connections, onlySecretsAboutToExpire]) => {
                if (!onlySecretsAboutToExpire) {
                    return connections;
                }

                return connections.filter((conn) => {
                    if (!conn.connection.secret || !conn.connection.secret.expiresAt) {
                        return false;
                    }

                    return this.secretIsAboutToExpire(new UTCDate(conn.connection.secret.expiresAt));
                });
            }),
            map((connections) => {
                this.isLoading.set(false);
                return connections;
            }),
            catchError(() => {
                this.isLoading.set(false);
                this.toastrService.error('Failed to load connections.', 'Load Failed');
                return of([] as GetConnectionResponse[]);
            }),
        );
        return connections;
    }

    public onRefreshSecret(element: GetConnectionResponse): void {
        if (!element.connection.id || !element.connection.secret?.clientId) {
            return;
        }
        if (element.connection.secret) {
            element.connection.secret.state = 'Processing';
        }
        this.connectionAutomationService
            .refreshSecret(element.businessDomain.id, {
                clientId: element.connection.secret.clientId,
                secretName: element.connection.secret.displayName,
            })
            .subscribe({
                next: () => {
                    this.toastrService.success('The secret was refreshed.', 'Refresh Secret Succeded');
                    this.refresh$.next();
                },
                error: (err) => {
                    this.toastrService.error(
                        'An unexpected error occurred while refreshing the secret.',
                        'Refresh Secret Failed',
                    );
                    this.refresh$.next();
                    return throwError(() => err);
                },
            });
    }

    protected retryFailedConnection(element: GetConnectionResponse): void {
        if (element.connection.state !== ConnectionState.Failed || element.connection.type !== 'DataLake') {
            return;
        }

        this.router.navigate(['/connections/create'], {
            queryParams: {
                connectionAlias: element.connection.alias,
                businessDomainId: element.businessDomain.id,
                dataLakePath: element.connection.host,
            },
        });
    }

    protected getConnectionDisplayName(connection: GetConnectionResponse): string {
        return connection.connection.alias;
    }

    protected getBackgroundColorForExpiresAtCell(row: GetConnectionResponse): string {
        if (!row.connection.secret || !row.connection.secret.expiresAt) {
            return '#f7f7f7';
        }

        if (row.connection.secret.isExpired) {
            return '#ff6464';
        }

        if (this.secretIsAboutToExpire(new UTCDate(row.connection.secret.expiresAt))) {
            return '#f3bfbf';
        }

        return '#f7f7f7';
    }

    protected getFontWeightForExpiresAtCell(row: GetConnectionResponse): 'normal' | 'bold' {
        if (!row.connection.secret) {
            return 'normal';
        }

        return row.connection.secret.isExpired ? 'bold' : 'normal';
    }

    protected getFormattedExpirationDate(row: GetConnectionResponse): string {
        if (!row.connection.secret || !row.connection.secret.expiresAt) {
            return '-';
        }

        const expiresAt = new UTCDate(row.connection.secret.expiresAt);
        return format(expiresAt, 'yyyy/MM/dd HH:mm');
    }

    private secretIsAboutToExpire(secretExpiresAt: Date): boolean {
        // Returns `date - now` in days (if `date` is in the future, the returned value will be positive)
        const getDaysBetweenNowAndDate = (date: Date): number => {
            const utcNow = new UTCDate(UTCDate.now());
            const daysBetweenNowAndDate = differenceInDays(date, utcNow);
            return daysBetweenNowAndDate;
        };

        const daysBetweenNowAndExpiration = getDaysBetweenNowAndDate(secretExpiresAt);
        const isAboutToExpire = daysBetweenNowAndExpiration < this.DAYS_NEAR_EXPIRATION;
        return isAboutToExpire;
    }
}
